import React, { useState, useEffect, useRef, useMemo, useCallback, useContext } from 'react';
import { useLockConfig, checkLocked } from '../hooks/useLockConfig';
import {
  Typography, Card, Descriptions, Tag, Button, Radio, InputNumber, Input,
  Space, Divider, Spin, message, Select, Progress, Alert, List, Avatar, Modal, Dropdown, DatePicker, Form, Table, Checkbox, Collapse, Popconfirm, Switch, Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import { SaveOutlined, CommentOutlined, UserOutlined, LinkOutlined, PlusOutlined, PrinterOutlined, EditOutlined, DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import CaseCallPane from '../components/CaseCallPane';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { UserContext } from '../App';
import { ProjectFyi } from '../components/ProjectParts';
import { formatTS } from '../utils/time';
import CoachingCreateModal from '../components/CoachingCreateModal';
import CommentBox, { CommentAttachments } from '../components/CommentBox';
import LinkifiedText from '../components/LinkifiedText';
import { PaperClipOutlined } from '@ant-design/icons';
import { useBackFallback, usePageLabel } from '../components/BackChip';

const STATUS_OPTIONS = [
  { label: 'Draft',             value: 'assigned' },
  { label: 'Completed',         value: 'completed' },
  { label: 'Rescore Requested', value: 'rescore_requested' },
  { label: 'Canceled',          value: 'canceled' },
];

const LOCKED_STATUSES = ['completed', 'canceled'];

// "Use to Train AI Autoscore" switch: hidden while it is not used.
const SHOW_TRAIN_AI_TOGGLE = false;

const getStatusOptions = (currentStatus) => {
  if (currentStatus === 'canceled') {
    // Currently unreachable: the status Select only renders when the status is
    // not in LOCKED_STATUSES, and canceled is locked, so a cancelled case has no
    // status control at all. Kept correct rather than deleted in case that
    // changes -- reopening should return the case to Draft. It previously
    // offered in_progress, which evaluations do not use.
    return STATUS_OPTIONS.filter((o) =>
      ['canceled', 'assigned'].includes(o.value)
    );
  }
  if (currentStatus === 'rescore_requested') {
    return STATUS_OPTIONS;
  }
  return STATUS_OPTIONS.filter((o) => o.value !== 'rescore_requested');
};
const STATUS_COLOR = {
  assigned: 'blue', in_progress: 'orange', completed: 'green',
  rescore_requested: 'purple', canceled: 'default',
};

function formatDuration(seconds) {
  if (seconds == null) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function questionMaxPts(q) {
  if (q.QuestionType === 'picklist') {
    return Math.max(0, ...(q.Options || []).map((o) => o.Points));
  }
  return q.MaxPoints || 0;
}

// Linked coaching sessions: colour and label by status, as on the other pages.
const SESSION_STATUS_COLOR = { draft: 'default', active: 'blue', completed: 'green', canceled: 'red' };
const SESSION_STATUS_LABEL = { draft: 'Draft', active: 'Active', completed: 'Completed', canceled: 'Canceled' };

export default function CasePage() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const currentUser = useContext(UserContext);
  // The Back chip (Layout): back where you came from, else this list.
  const staffEvals = (currentUser?.CanEditEvals ?? 0) >= 1;
  useBackFallback(staffEvals ? '/cases' : '/my-evals', staffEvals ? 'Eval Search' : 'My Evaluations');
  usePageLabel(`Evaluation #${caseId}`);
  const isAgent     = currentUser?.Role === 'agent';
  const isAdmin     = currentUser?.Role === 'admin';

  const [caseData, setCaseData]   = useState(null);
  const [formData, setFormData]   = useState(null);
  const [responses, setResponses] = useState({});       // { [questionId]: { option_id, response_text, points_earned } }
  const [occurrences, setOccurrences] = useState({});  // { [sectionId]: 0 | 1 }
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [pendingStatus, setPendingStatus]   = useState(null);
  const [pendingAssign, setPendingAssign]   = useState(null);
  const [reassigning, setReassigning]       = useState(false);
  const [evalUsers, setEvalUsers]           = useState([]);
  const [comments, setComments]   = useState([]);
  // Collapsed by default, and remembered per browser. It is a reading aid, not
  // something everyone wants taking a quarter of the width every time.
  // Mirrors the stored value so the switch reflects the server, not the click.
  const [trainAI, setTrainAI]         = useState(false);
  const [trainSaving, setTrainSaving] = useState(false);

  // False only for an evaluation created without a call (no ContactID).
  const hasCall = !(caseData && !caseData.ContactID);

  const [callPaneOpen, setCallPaneOpen] = useState(() => {
    try { return localStorage.getItem('casePaneOpen') === '1'; } catch { return false; }
  });
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [editComment, setEditComment] = useState({ id: null, text: '' });
  const [rescoreModalOpen, setRescoreModalOpen] = useState(false);
  const [rescoreComment, setRescoreComment]     = useState('');

  const [secRows,    setSecRows]    = useState([]);
  const [secLoading, setSecLoading] = useState(false);
  const [requestingRescore, setRequestingRescore] = useState(false);
  const [acknowledging,    setAcknowledging]    = useState(false);
  const [canceling,        setCanceling]        = useState(false);

  const [externalRefDraft, setExternalRefDraft] = useState('');
  const [savingExtRef,     setSavingExtRef]     = useState(false);

  const [printOptOpen,  setPrintOptOpen]  = useState(false);
  const [printComments, setPrintComments] = useState(false);
  // With Include comments: leave out the comments of the agent the eval is for.
  const [printHideAgent, setPrintHideAgent] = useState(false);
  // Completed evaluations: show only the questions that carry points and did
  // not get all of them, plus every point question of a section marked
  // Occurrence: Yes; sections left with nothing are hidden.
  const [missedOnly, setMissedOnly] = useState(false);
  const [printCoaching, setPrintCoaching] = useState(false);

  const lockConfig    = useLockConfig();
  const isEvalLocked  = checkLocked(lockConfig, 'eval', caseData?.CallDate);

  // Numbers close at the start of the month for the month before, so the agent
  // cannot dispute an evaluation of a call from an earlier month. They can still
  // acknowledge it. Scoped to the agent: an evaluator or admin is unaffected.
  //
  // Separate from the Record Lock Date, which hides the dispute control for
  // everyone once a month is locked. This rule is narrower and needed even
  // with no lock set: it closes the previous month to the person the
  // evaluation is about, the moment the new one begins.
  //
  // (It used to say the lock did not apply here because useLockConfig fetched
  // for admins only. That stopped being true when the config was opened to
  // every signed-in user.)
  const isCaseAgent      = currentUser?.ID === caseData?.AgentUserID;
  const callInPriorMonth = !!caseData?.CallDate
    && dayjs(caseData.CallDate).isBefore(dayjs().startOf('month'), 'day');
  const disputeClosedByMonth = isCaseAgent && callInPriorMonth;

  const canReassign       = !isAgent && currentUser != null && (currentUser.CanEditEvals ?? 0) >= 2;
  const isManager         = (currentUser?.CanEditEvals ?? 0) >= 3;
  const canEdit           = canReassign && (isManager || caseData?.AssignedToUserID === currentUser?.ID);
  const canManageCoaching = (currentUser?.CanEditCoaching ?? 0) >= 2;

  const evalOptions = (() => {
    const base = evalUsers.map((u) => ({ label: u.DisplayName || u.Email, value: u.ID }));
    if (caseData?.AssignedToUserID && !base.some((u) => u.value === caseData.AssignedToUserID)) {
      base.unshift({ value: caseData.AssignedToUserID, label: caseData.AssignedToName || 'Unknown' });
    }
    return base;
  })();

  // Coaching sessions linked to this case's ContactID
  const [coachSessions, setCoachSessions]     = useState([]);
  const [coachLoading, setCoachLoading]       = useState(false);
  const [createCoachOpen, setCreateCoachOpen] = useState(false);
  // Shown once, on the transition into completed - same flow as an error
  // submission: the updated history including this evaluation, then the choice
  // of whether it warrants a coaching session.
  const [completedModalOpen, setCompletedModalOpen] = useState(false);

  // Admin and QA admin only: this decides what the model is taught to
  // imitate, which is a different question from who may score a call. And only
  // once the evaluation is finished -- a draft is still changing.
  const canTrainAI = ['admin', 'qa_admin'].includes(currentUser?.Role)
    && ['completed', 'canceled'].includes(caseData?.Status);

  const handleTrainToggle = async (checked) => {
    setTrainSaving(true);
    try {
      const res = await api.patch(`/api/cases/${caseId}/train-ai`, { use_to_train: checked });
      setTrainAI(res.data.use_to_train);
      message.success(checked ? 'Marked as an AI training example.' : 'Removed from AI training examples.');
    } catch (e) {
      // Left showing the server's value rather than the click, so a refused
      // change does not look like it took.
      message.error(e.response?.data?.error || 'Could not update.');
    } finally {
      setTrainSaving(false);
    }
  };

  const togglePane = (open) => {
    setCallPaneOpen(open);
    try { localStorage.setItem('casePaneOpen', open ? '1' : '0'); } catch { /* private browsing */ }
  };

  const loadCoachSessions = useCallback(() => {
    if (!caseData?.ContactID) return;
    setCoachLoading(true);
    api.get(`/api/coaching/sessions/for-contact/${caseData.ContactID}`)
      .then((r) => setCoachSessions(r.data))
      .catch(() => {})
      .finally(() => setCoachLoading(false));
  }, [caseData?.ContactID]);

  useEffect(() => { loadCoachSessions(); }, [loadCoachSessions]);


  const loadComments = useCallback(() => {
    api.get(`/api/cases/${caseId}/comments`)
      .then((res) => setComments(res.data))
      .catch(() => {});
  }, [caseId]);

  // Load eval-eligible users scoped to the case's campaign, including the current assignee
  useEffect(() => {
    if (!canReassign || !caseData) return;
    const params = { can_edit_evals: 1 };
    if (caseData.CampaignID)        params.campaign_id    = caseData.CampaignID;
    if (caseData.AssignedToUserID)  params.include_user_id = caseData.AssignedToUserID;
    api.get('/api/users', { params })
      .then((res) => setEvalUsers(res.data))
      .catch(() => {});
  }, [canReassign, caseData]);

  useEffect(() => {
    api.get(`/api/cases/${caseId}`)
      .then((res) => {
        setCaseData(res.data.case);
        // Seeded from the server so the switch shows the stored value.
        setTrainAI(!!res.data.case?.UseToTrainAI);
        setPendingStatus(res.data.case.Status);
        setPendingAssign(res.data.case.AssignedToUserID ?? null);
        setFormData(res.data.form ?? null);
        setExternalRefDraft(res.data.case.ExternalRef || '');

        // Seed responses from saved data
        const init = {};
        for (const sec of res.data.form?.Sections ?? []) {
          for (const q of sec.Questions ?? []) {
            if (q.Response) {
              init[q.ID] = {
                option_id:     q.Response.OptionID   ?? null,
                response_text: q.Response.ResponseText ?? '',
                points_earned: q.Response.PointsEarned ?? 0,
              };
            }
          }
        }
        setResponses(init);
        setOccurrences(
          Object.fromEntries(
            Object.entries(res.data.occurrences || {}).map(([k, v]) => [parseInt(k, 10), v])
          )
        );
      })
      .catch((err) => {
        const status = err.response?.status;
        if (status === 403) {
          message.error('You do not have access to this evaluation.');
          navigate(-1);
        } else if (status === 404) {
          message.error('Evaluation not found.');
          navigate(-1);
        } else {
          message.error(err.response?.data?.error || 'Failed to load evaluation.');
        }
      })
      .finally(() => setLoading(false));
    loadComments();
  }, [caseId, loadComments]);

  const loadSecHistory = useCallback(() => {
    if (!caseData?.AgentName || !caseData?.CallDate) return;
    const endDate   = dayjs(caseData.CallDate.slice(0, 10));
    const startDate = endDate.subtract(3, 'month').startOf('month');
    setSecLoading(true);
    api.get('/api/eval-analytics/agent-sections', {
      params: {
        group_by:        'month',
        // Always by user. AgentName is the QAUsers display name, a label only.
        agent_user_id: caseData.AgentUserID,
        campaigns:       caseData.Campaign || undefined,
        call_date_start: startDate.format('YYYY-MM-DD'),
        call_date_end:   endDate.format('YYYY-MM-DD'),
        // Once completed, this evaluation belongs in its own history: the
        // panel stops answering "how has the agent done otherwise" and starts
        // reporting where they now stand. Same rule as the error page.
        ...(caseData.Status === 'completed' ? {} : { exclude_id: caseId }),
      },
    })
      .then((r) => setSecRows(r.data))
      .catch(() => {})
      .finally(() => setSecLoading(false));
  }, [caseData, caseId]);

  useEffect(() => { loadSecHistory(); }, [loadSecHistory]);

  // Per-evaluation rows for the history list. Same filters as the pivot above,
  // so the two describe the same set -- this one names the evaluations and
  // whether each raised a coaching session.
  const [secCaseRows, setSecCaseRows] = useState([]);
  const [secCaseLoading, setSecCaseLoading] = useState(false);

  // Active QA-related compliance records for the agent: completed incidents
  // flagged QA Related whose QA Related Expires On date has not passed. From a
  // narrow read-only endpoint, so evaluators without compliance access still
  // see the list; only those with access get a link into the record.
  const [qaWarnings, setQaWarnings]               = useState([]);
  const [qaWarningsLoading, setQaWarningsLoading] = useState(false);
  // Not shown to the agent the evaluation is about (the API returns nothing
  // to them either).
  const isOwnEvaluation = !!caseData?.AgentUserID && caseData.AgentUserID === currentUser?.ID;
  useEffect(() => {
    if (!caseData?.AgentUserID || isOwnEvaluation) { setQaWarnings([]); return; }
    setQaWarningsLoading(true);
    api.get('/api/compliance/qa-related-active', { params: { employee_user_id: caseData.AgentUserID } })
      .then((res) => setQaWarnings(res.data || []))
      .catch(() => setQaWarnings([]))
      .finally(() => setQaWarningsLoading(false));
  }, [caseData?.AgentUserID, isOwnEvaluation]); // eslint-disable-line

  const loadSecCaseHistory = useCallback(() => {
    if (!caseData?.AgentUserID || !caseData?.CallDate) return;
    const endDate   = dayjs(caseData.CallDate.slice(0, 10));
    const startDate = endDate.subtract(3, 'month').startOf('month');
    setSecCaseLoading(true);
    api.get('/api/eval-analytics/agent-sections', {
      params: {
        group_by:        'section_case',
        agent_user_id:   caseData.AgentUserID,
        campaigns:       caseData.Campaign || undefined,
        call_date_start: startDate.format('YYYY-MM-DD'),
        call_date_end:   endDate.format('YYYY-MM-DD'),
        ...(caseData.Status === 'completed' ? {} : { exclude_id: caseId }),
      },
    })
      .then((r) => setSecCaseRows(r.data || []))
      .catch(() => setSecCaseRows([]))
      .finally(() => setSecCaseLoading(false));
  }, [caseData, caseId]);

  useEffect(() => { loadSecCaseHistory(); }, [loadSecCaseHistory]);

  const handleSaveExternalRef = async () => {
    setSavingExtRef(true);
    try {
      await api.patch(`/api/cases/${caseId}/external-ref`, { external_ref: externalRefDraft.trim() || null });
      setCaseData((prev) => ({ ...prev, ExternalRef: externalRefDraft.trim() || null }));
      message.success('External Ref saved.');
    } catch {
      message.error('Failed to save External Ref.');
    } finally {
      setSavingExtRef(false);
    }
  };

  const handleAddComment = async (text, files = []) => {
    setAddingComment(true);
    try {
      const res = await api.post(`/api/cases/${caseId}/comments`, { comment: text });
      const commentId = res.data.id;
      for (const f of files) {
        const fd = new FormData();
        fd.append('comment_type', 'case');
        fd.append('comment_id', String(commentId));
        fd.append('file', f.file, f.name);
        await api.post('/api/attachments', fd);
      }
      setNewComment('');
      loadComments();
    } catch {
      message.error('Failed to add comment.');
    } finally {
      setAddingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await api.delete(`/api/cases/${caseId}/comments/${commentId}`);
      loadComments();
    } catch {
      message.error('Failed to delete comment.');
    }
  };

  const handleSaveComment = async () => {
    const text = editComment.text.trim();
    if (!text) return;
    try {
      await api.put(`/api/cases/${caseId}/comments/${editComment.id}`, { comment: text });
      setEditComment({ id: null, text: '' });
      loadComments();
    } catch (err) {
      message.error(err?.response?.data?.error || 'Failed to update comment.');
    }
  };

  const setResponse = useCallback((qId, patch) => {
    setResponses((prev) => ({ ...prev, [qId]: { ...prev[qId], ...patch } }));
  }, []);

  const handleOptionSelect = useCallback((q, optionId) => {
    const opt = (q.Options || []).find((o) => o.ID === optionId);
    setResponse(q.ID, { option_id: optionId, points_earned: opt?.Points ?? 0 });
  }, [setResponse]);

  const { totalEarned, totalMax } = useMemo(() => {
    let earned = 0, max = 0;
    for (const sec of formData?.Sections ?? []) {
      for (const q of sec.Questions ?? []) {
        earned += responses[q.ID]?.points_earned ?? 0;
        max    += questionMaxPts(q);
      }
    }
    return { totalEarned: earned, totalMax: max };
  }, [formData, responses]);

  const pct = totalMax > 0 ? Math.round((totalEarned / totalMax) * 100) : 0;
  // Points can have decimals (2.5); sums of them are shown to 2 places.
  const fmtPts = (v) => Math.round((v || 0) * 100) / 100;

  const handleSave = async () => {
    // Validate mandatory questions
    const missing = [];
    for (const sec of formData?.Sections ?? []) {
      for (const q of sec.Questions ?? []) {
        if (!q.IsMandatory) continue;
        const r = responses[q.ID];
        const answered = q.QuestionType === 'picklist'
          ? r?.option_id != null
          : (q.QuestionType === 'text' || q.QuestionType === 'date')
            ? r?.response_text?.trim()
            : r !== undefined;
        if (!answered) missing.push(`"${q.QuestionText}" (${sec.Name})`);
      }
    }
    if (missing.length > 0) {
      message.error(`Please answer all mandatory questions: ${missing.join(', ')}`);
      return;
    }

    setSaving(true);
    try {
      const body = Object.entries(responses).map(([qId, r]) => ({
        question_id:   parseInt(qId, 10),
        option_id:     r.option_id ?? null,
        response_text: r.response_text ?? '',
        points_earned: r.points_earned ?? 0,
      }));
      const occBody = Object.entries(occurrences).map(([secId, v]) => ({
        section_id:  parseInt(secId, 10),
        occurrences: v ?? 0,
      }));
      await api.put(`/api/cases/${caseId}/responses`, { responses: body, occurrences: occBody });
      if (newComment.trim()) {
        await api.post(`/api/cases/${caseId}/comments`, { comment: newComment.trim() });
        setNewComment('');
        loadComments();
      }
      message.success('Saved.');
    } catch {
      message.error('Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleSetStatus = async () => {
    const newStatus = pendingStatus;
    if (!newStatus || newStatus === caseData?.Status) return;

    if (newStatus === 'rescore_requested') {
      setRescoreModalOpen(true);
      return;
    }

    if (newStatus === 'completed') {
      const missing = [];
      for (const sec of formData?.Sections ?? []) {
        for (const q of sec.Questions ?? []) {
          if (!q.IsMandatory) continue;
          const r = responses[q.ID];
          const answered = q.QuestionType === 'picklist'
            ? r?.option_id != null
            : r !== undefined;
          if (!answered) missing.push(`"${q.QuestionText}" (${sec.Name})`);
        }
      }
      if (missing.length > 0) {
        message.error(`Cannot mark as Completed — mandatory questions unanswered: ${missing.join(', ')}`);
        return;
      }
    }

    const wasCompleted = caseData?.Status === 'completed';
    setUpdatingStatus(true);
    try {
      await api.put(`/api/cases/${caseId}`, { status: newStatus });
      const now = new Date().toISOString().replace('T', ' ').slice(0, 19);
      setCaseData((prev) => ({
        ...prev,
        Status: newStatus,
        CompletedAt: ['completed', 'canceled'].includes(newStatus) ? now : prev.CompletedAt,
        RescoreRequestedAt: newStatus === 'rescore_requested' ? now : prev.RescoreRequestedAt,
        UpdatedAt: now,
      }));
      message.success('Status updated.');
      if (newStatus === 'completed' && wasCompleted !== true) {
        // Refresh the section history so the modal reflects this evaluation
        // rather than the state before it was scored.
        // setCaseData above flips Status to completed, which re-runs
        // loadSecHistory without exclude_id, so the modal reads a history that
        // counts this evaluation.
        setCompletedModalOpen(true);
      }
    } catch {
      message.error('Failed to update status.');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleReassign = async () => {
    if (pendingAssign === (caseData?.AssignedToUserID ?? null)) return;
    setReassigning(true);
    try {
      const res = await api.patch(`/api/cases/${caseId}/reassign`, { assigned_to_user_id: pendingAssign });
      setCaseData((prev) => ({ ...prev, AssignedToUserID: pendingAssign, AssignedToName: res.data.assignedToName }));
      message.success('Evaluation reassigned.');
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to reassign.');
    } finally {
      setReassigning(false);
    }
  };

  const handleSaveAll = async (confirmed = false) => {
    if (!confirmed && pendingStatus === 'canceled' && pendingStatus !== caseData?.Status) {
      Modal.confirm({
        title: 'Are you sure you want to change the Status to Canceled?',
        content: 'Canceling cannot be undone.',
        okText: 'Yes',
        cancelText: 'No',
        onOk: () => handleSaveAll(true),
      });
      return;
    }
    if (pendingStatus === 'completed' && canEdit && !isLocked) {
      const missing = [];
      for (const sec of formData?.Sections ?? []) {
        for (const q of sec.Questions ?? []) {
          if (!q.IsMandatory) continue;
          const r = responses[q.ID];
          const answered = q.QuestionType === 'picklist'
            ? r?.option_id != null
            : (q.QuestionType === 'text' || q.QuestionType === 'date')
              ? r?.response_text?.trim()
              : r !== undefined;
          if (!answered) missing.push(`"${q.QuestionText}" (${sec.Name})`);
        }
      }
      if (missing.length > 0) {
        message.error(`Cannot mark as Completed — mandatory questions unanswered: ${missing.join(', ')}`);
        return;
      }
    }
    setSaving(true);
    try {
      if (canEdit && !isLocked) {
        const body = Object.entries(responses).map(([qId, r]) => ({
          question_id:   parseInt(qId, 10),
          option_id:     r.option_id ?? null,
          response_text: r.response_text ?? '',
          points_earned: r.points_earned ?? 0,
        }));
        const occBody = Object.entries(occurrences).map(([secId, v]) => ({
          section_id:  parseInt(secId, 10),
          occurrences: v ?? 0,
        }));
        await api.put(`/api/cases/${caseId}/responses`, { responses: body, occurrences: occBody });
        if (newComment.trim()) {
          await api.post(`/api/cases/${caseId}/comments`, { comment: newComment.trim() });
          setNewComment('');
          loadComments();
        }
      }
      if (pendingStatus && pendingStatus !== caseData?.Status) {
        const wasCompleted = caseData?.Status === 'completed';
        await api.put(`/api/cases/${caseId}`, { status: pendingStatus });
        const now = new Date().toISOString().replace('T', ' ').slice(0, 19);
        setCaseData((prev) => ({
          ...prev,
          Status: pendingStatus,
          CompletedAt: ['completed', 'canceled'].includes(pendingStatus) ? now : prev.CompletedAt,
          RescoreRequestedAt: pendingStatus === 'rescore_requested' ? now : prev.RescoreRequestedAt,
          UpdatedAt: now,
        }));
        // Scoring an evaluation and completing it happens here, through Save,
        // not through the standalone status control -- so the coaching prompt
        // has to fire on both paths or it never appears in the normal flow.
        if (pendingStatus === 'completed' && !wasCompleted) {
          setCompletedModalOpen(true);
        }
      }
      if (pendingAssign !== (caseData?.AssignedToUserID ?? null)) {
        const res = await api.patch(`/api/cases/${caseId}/reassign`, { assigned_to_user_id: pendingAssign });
        setCaseData((prev) => ({ ...prev, AssignedToUserID: pendingAssign, AssignedToName: res.data.assignedToName }));
      }
      message.success('Saved.');
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleRequestRescore = async () => {
    const comment = rescoreComment.trim();
    if (!comment) {
      message.error('A comment is required when requesting a rescore.');
      return;
    }
    setRequestingRescore(true);
    try {
      await api.post(`/api/cases/${caseId}/comments`, { comment });
      await api.put(`/api/cases/${caseId}`, { status: 'rescore_requested', rescore_note: comment });
      const now = new Date().toISOString().replace('T', ' ').slice(0, 19);
      setCaseData((prev) => ({ ...prev, Status: 'rescore_requested', RescoreRequestedAt: now, RescoreNote: comment, UpdatedAt: now }));
      // The Status control is bound to pendingStatus, not to caseData.Status.
      // Without this it kept showing the old status until the page was
      // reloaded, which read as the request not having gone through.
      setPendingStatus('rescore_requested');
      setRescoreModalOpen(false);
      setRescoreComment('');
      loadComments();
      message.success('Rescore requested.');
    } catch {
      message.error('Failed to request rescore.');
    } finally {
      setRequestingRescore(false);
    }
  };

  const handlePrint = () => setPrintOptOpen(true);
  // Print is deferred to the modal's afterClose rather than a timer. See the
  // matching comment on ErrorSubmissionPage: window.print() blocks the JS
  // thread, so firing it 150ms after closing the modal could land mid-unmount
  // and strand Ant's body scroll lock and mask, leaving the page frozen.
  const printTitleRef   = useRef(null);
  const pendingPrintRef = useRef(false);

  useEffect(() => {
    const restore = () => {
      if (printTitleRef.current != null) {
        document.title = printTitleRef.current;
        printTitleRef.current = null;
      }
    };
    window.addEventListener('afterprint', restore);
    return () => {
      window.removeEventListener('afterprint', restore);
      restore();
    };
  }, []);

  const doPrint = () => {
    const slug  = (str) => (str || '').replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
    const date  = caseData?.CallDate?.slice(0, 10) || dayjs().format('YYYY-MM-DD');
    const title = `${date}_${caseId}_Eval_${slug(caseData?.Campaign)}_${slug(caseData?.AgentName)}`;
    printTitleRef.current = document.title;
    document.title = title;
    pendingPrintRef.current = true;
    setPrintOptOpen(false);
  };

  const printAfterModalClosed = () => {
    if (!pendingPrintRef.current) return;   // a plain cancel must not print
    pendingPrintRef.current = false;
    window.print();
  };

  const handleAcknowledge = async () => {
    setAcknowledging(true);
    try {
      await api.post(`/api/cases/${caseId}/acknowledge`);
      message.success('Acknowledged.');
      const res = await api.get(`/api/cases/${caseId}`);
      setCaseData(res.data.case);
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to acknowledge.');
    } finally {
      setAcknowledging(false);
    }
  };

  // Admin / qa_admin: undo an acknowledgement so the evaluation can be
  // disputed. Not shown under the record lock date (checked on the server too).
  const [unacking, setUnacking] = useState(false);
  const handleUnacknowledge = async () => {
    setUnacking(true);
    try {
      await api.post(`/api/cases/${caseId}/unacknowledge`);
      setCaseData((prev) => ({ ...prev, AcknowledgedAt: null, AutoAcknowledged: 0, AcknowledgedByName: null }));
      loadComments();
      message.success('Acknowledgement undone.');
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to undo.');
    } finally {
      setUnacking(false);
    }
  };

  const handleCancelCase = async () => {
    setCanceling(true);
    try {
      await api.put(`/api/cases/${caseId}`, { status: 'canceled' });
      const now = new Date().toISOString().replace('T', ' ').slice(0, 19);
      setCaseData((prev) => ({ ...prev, Status: 'canceled', CompletedAt: prev.CompletedAt ?? now, UpdatedAt: now }));
      setPendingStatus('canceled');
      message.success('Status set to Canceled.');
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to cancel.');
    } finally {
      setCanceling(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  const scoreColor    = pct >= 80 ? '#52c41a' : pct >= 60 ? '#faad14' : '#ff4d4f';
  const isLocked      = !canEdit || LOCKED_STATUSES.includes(caseData?.Status);
  const isAcknowledged = !!caseData?.AcknowledgedAt;

  const disputeDeadline = caseData?.CompletedAt
    ? dayjs(caseData.CompletedAt).add(formData?.DisputeWindowDays ?? 4, 'day')
    : null;

  const disputeWindowClosed = (() => {
    if (isAdmin) return false;
    const base = formData?.DisputeWindowDays ?? 4;
    if (base === 0) return true;
    const deadline = caseData?.CompletedAt ? dayjs(caseData.CompletedAt).add(base, 'day') : null;
    return deadline ? dayjs().isAfter(deadline) : false;
  })();

  const isEvalAgent = isAgent || currentUser?.ID === caseData?.AgentUserID;
  const isViewOnlyEvals = (currentUser?.CanEditEvals ?? 0) === 1 && currentUser?.ID !== caseData?.AgentUserID;
  const agentCanComment = !isViewOnlyEvals && (
    !isEvalAgent
    || caseData?.Status === 'rescore_requested'
    || (caseData?.Status === 'completed' && !disputeWindowClosed && !isAcknowledged)
  );

  const secHistoryRange = (caseData?.AgentName && caseData?.CallDate) ? (() => {
    const end   = dayjs(caseData.CallDate.slice(0, 10));
    const start = end.subtract(3, 'month').startOf('month');
    return { start, end };
  })() : null;

  const secMonths  = [...new Set(secRows.map((r) => r.MonthKey))].sort();
  const secMLabels = {};
  secRows.forEach((r) => { secMLabels[r.MonthKey] = r.Month; });

  const secByKey     = {};
  const secKeyOrder  = [];
  secRows.forEach((r) => {
    const k = r.SectionName;
    if (!secByKey[k]) { secByKey[k] = { _key: k, SectionName: r.SectionName }; secKeyOrder.push(k); }
    secByKey[k][r.MonthKey] = r.Occurrences ?? 0;
  });
  const secPivotData = secKeyOrder.map((k) => secByKey[k]);

  const secPivotCols = [
    { title: 'Section', dataIndex: 'SectionName', key: '__sec', ellipsis: true },
    ...secMonths.map((mk) => ({
      title: secMLabels[mk] || mk, key: mk, width: 110, align: 'center',
      render: (_, r) => {
        const v = r[mk];
        if (v == null || v === 0) return <span style={{ color: '#d9d9d9' }}>0</span>;
        return <Tag color="orange" style={{ margin: 0 }}>{v}</Tag>;
      },
    })),
  ];

  return (
    <div style={{ paddingRight: callPaneOpen && caseData?.ContactID ? 'calc(25vw + 16px)' : 0, transition: 'padding-right .15s' }}>
      <style>{`
        @media print {
          html, body { margin: 0 !important; padding: 0 !important; background: white !important; }
          .ant-layout { min-height: 0 !important; background: white !important; }
          .ant-layout-header { display: none !important; }
          .ant-layout-content { padding: 0 !important; margin: 0 !important; background: white !important; }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          .ant-space-vertical { gap: 12px !important; }
          .ant-collapse-content-hidden { display: block !important; opacity: 1 !important; }
          .ant-collapse-content { height: auto !important; overflow: visible !important; }
          .ant-collapse-expand-icon { display: none !important; }
          .ant-card { break-inside: avoid; page-break-inside: avoid; }
          /* Fix Descriptions table — print viewport collapses responsive columns */
          .ant-descriptions-bordered .ant-descriptions-view table {
            table-layout: auto !important;
            width: 100% !important;
          }
          .ant-descriptions-bordered .ant-descriptions-item-label,
          .ant-descriptions-bordered .ant-descriptions-item-content {
            white-space: normal !important;
            word-break: break-word !important;
            min-width: 60px !important;
            padding: 4px 8px !important;
          }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
        .print-only { display: none; }
      `}</style>

      {/* Print-only header */}
      <div className="print-only" style={{ marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid #000' }}>
        <div><strong>Evaluation #{caseId} &mdash; Status: {STATUS_OPTIONS.find((o) => o.value === caseData?.Status)?.label ?? caseData?.Status}</strong></div>
        <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>Printed: {dayjs().format('YYYY-MM-DD HH:mm')}</div>
      </div>

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: (caseData?.IsCalibration || caseData?.IsFeedbackOnly) ? 8 : 16, flexWrap: 'wrap' }}>
        {(currentUser?.CanEditEvals ?? 0) >= 1 && (
          <Button className="no-print" icon={<PrinterOutlined />} onClick={handlePrint}>Print</Button>
        )}
        {caseData?.IsCalibration ? (
          <Tag color="purple" style={{ fontSize: 14, fontWeight: 700, padding: '2px 10px' }}>Calibration</Tag>
        ) : caseData?.IsFeedbackOnly ? (
          <Tag color="purple" style={{ fontSize: 14, fontWeight: 700, padding: '2px 10px' }}>Feedback Only</Tag>
        ) : (
          <Tag color="blue" style={{ fontSize: 14, fontWeight: 700, padding: '2px 10px' }}>Evaluation</Tag>
        )}
        <Typography.Title level={4} style={{ margin: 0 }}>
          #{caseId} — {caseData && !caseData.ContactID ? 'Ref ' : 'Contact '}
          {caseData?.ContactID ? (
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  {
                    key: 'navigate',
                    label: 'Navigate to contact',
                    icon: <LinkOutlined />,
                    onClick: () => navigate(`/calls/${caseData.ContactID}`),
                  },
                  {
                    key: 'new-window',
                    label: 'Open in new window',
                    icon: <LinkOutlined />,
                    onClick: () => window.open(`/calls/${caseData.ContactID}`, '_blank'),
                  },

                ],
              }}
            >
              <Typography.Link style={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
                {caseData.ContactID}
              </Typography.Link>
            </Dropdown>
          ) : (caseData?.ExternalRef || '—')}
        </Typography.Title>
        <Tag color={STATUS_COLOR[caseData?.Status] ?? 'default'} style={{ fontSize: 13 }}>
          Status: {STATUS_OPTIONS.find((o) => o.value === caseData?.Status)?.label ?? caseData?.Status}
        </Tag>
        {caseData?.CreatedAt && (
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            Created on: {formatTS(caseData.CreatedAt)}
          </Typography.Text>
        )}
        {caseData?.UpdatedAt && (
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            Last modified: {formatTS(caseData.UpdatedAt)}
          </Typography.Text>
        )}
        {caseData?.CompletedAt && (
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            Completed on: {formatTS(caseData.CompletedAt)}
          </Typography.Text>
        )}
        {caseData?.RescoreRequestedAt && (
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            Rescore requested on: {formatTS(caseData.RescoreRequestedAt)}
          </Typography.Text>
        )}
        {caseData?.CompletedAt && formData?.DisputeWindowDays > 0 && !disputeWindowClosed && (
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            Dispute by: {dayjs(caseData.CompletedAt).add(formData?.DisputeWindowDays ?? 4, 'day').format('YYYY-MM-DD')}
          </Typography.Text>
        )}
      </div>

      {/* Sticky QA action bar — status, assigned to, save */}
      {canEdit && (
        <div className="no-print" style={{
          position: 'sticky', top: 0, zIndex: 20,
          background: 'var(--ant-color-bg-layout)',
          borderBottom: '1px solid var(--ant-color-border)',
          padding: '8px 0', marginBottom: 16,
        }}>
          <Space wrap align="center">
            {!LOCKED_STATUSES.includes(caseData?.Status) && (
              <Space size={4} align="center">
                <Typography.Text style={{ fontSize: 13, whiteSpace: 'nowrap' }}>Status:</Typography.Text>
                <Select
                  value={pendingStatus}
                  onChange={setPendingStatus}
                  options={getStatusOptions(caseData?.Status)}
                  style={{ width: 170 }}
                />
              </Space>
            )}
            <Space size={4} align="center">
              <Typography.Text style={{ fontSize: 13, whiteSpace: 'nowrap' }}>Assigned To:</Typography.Text>
              <Select
                value={pendingAssign ?? undefined}
                onChange={(v) => setPendingAssign(v ?? null)}
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Assign to…"
                options={evalOptions}
                style={{ width: 200 }}
                disabled={LOCKED_STATUSES.includes(caseData?.Status)}
              />
            </Space>
            {!LOCKED_STATUSES.includes(caseData?.Status) && (
              <Button type="primary" icon={<SaveOutlined />} onClick={() => handleSaveAll()} loading={saving}>
                Save
              </Button>
            )}

            {/* Marks this evaluation as an example for the AI to learn from.
                Saves on toggle rather than waiting for Save, because a
                completed evaluation is read only and has no Save button to
                wait for -- which is most of the cases this applies to. */}
            {/* Hidden for now: not in use. The code behind it is kept --
                remove SHOW_TRAIN_AI_TOGGLE's false to bring it back. */}
            {SHOW_TRAIN_AI_TOGGLE && canTrainAI && (
              <Space size={6}>
                <Switch
                  size="small"
                  checked={!!trainAI}
                  loading={trainSaving}
                  onChange={handleTrainToggle}
                />
                <Tooltip title="Include this evaluation as an example when the AI scores future calls. Pick ones whose scoring and write-up you would want imitated.">
                  <Typography.Text style={{ fontSize: 13 }}>
                    Use to Train AI Autoscore <InfoCircleOutlined style={{ fontSize: 11, opacity: 0.45 }} />
                  </Typography.Text>
                </Tooltip>
              </Space>
            )}
          </Space>
        </div>
      )}

      {/* Sticky Acknowledge + Dispute bar — stacks below QA bar when both are visible */}
      {caseData?.Status === 'completed' && (currentUser?.ID === caseData?.AgentUserID || currentUser?.ID === caseData?.AssignedToUserID || currentUser?.Role === 'admin' || currentUser?.Role === 'qa_admin') && (
        <div className="no-print" style={{
          position: 'sticky', top: canEdit ? 41 : 0, zIndex: 19,
          background: 'var(--ant-color-bg-layout)',
          borderBottom: '1px solid var(--ant-color-border)',
          padding: '8px 0', marginBottom: 16,
        }}>
          <Space size={8} align="center">
            {!isAcknowledged
              ? <Button size="small" type="primary" loading={acknowledging} onClick={handleAcknowledge}>Acknowledge</Button>
              : <Typography.Text type="secondary" style={{ fontSize: 13 }}>Acknowledged on: {formatTS(caseData.AcknowledgedAt)}</Typography.Text>}
            {isAcknowledged && (currentUser?.Role === 'admin' || currentUser?.Role === 'qa_admin') && !isEvalLocked && (
              <Popconfirm
                title="Undo this acknowledgement?"
                description="The agent will be able to acknowledge or request a rescore again."
                okText="Undo"
                cancelText="No"
                onConfirm={handleUnacknowledge}
              >
                <Button size="small" loading={unacking}>Undo Acknowledgement</Button>
              </Popconfirm>
            )}
            {!isAcknowledged && !isEvalLocked && !caseData?.RescoreRequestedAt && !caseData?.IsFeedbackOnly && (formData?.DisputeWindowDays === 0
              ? <Typography.Text type="secondary" style={{ fontSize: 12 }}>This evaluation cannot be disputed.</Typography.Text>
              : disputeClosedByMonth
                ? <Typography.Text type="secondary" style={{ fontSize: 12 }}>Evaluations from a previous month can no longer be disputed.</Typography.Text>
                : disputeWindowClosed
                ? <Typography.Text type="secondary" style={{ fontSize: 12 }}>The dispute deadline has passed.</Typography.Text>
                : <Button size="small" onClick={() => setRescoreModalOpen(true)}>Request Rescore</Button>)}
            {(currentUser?.Role === 'admin' || currentUser?.Role === 'qa_admin') && !isEvalLocked && (
              <Popconfirm
                title="Are you sure you want to change the Status to Canceled?"
                description="Canceling cannot be undone."
                okText="Yes"
                cancelText="No"
                onConfirm={handleCancelCase}
              >
                <Button size="small" danger loading={canceling}>Set to Canceled</Button>
              </Popconfirm>
            )}
          </Space>
        </div>
      )}


      {/* Form name subtitle */}
      {formData && (
        <div style={{ marginBottom: 12 }}>
          <Typography.Text strong style={{ fontSize: 14 }}>
            Form: {formData.Name}{formData.Version ? ` (v${formData.Version})` : ''}
          </Typography.Text>
        </div>
      )}

      {/* Feedback Only banner */}
      {caseData?.IsFeedbackOnly ? (
        <div style={{ marginBottom: 16 }}>
          <Tag color="purple">Feedback Only — this evaluation is not counted in the agent's official score</Tag>
        </div>
      ) : null}

      {/* Calibration session banner */}
      {!!caseData?.IsCalibration && (
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Typography.Text strong>Calibration Session:</Typography.Text>
          <Typography.Link onClick={() => navigate(`/calibration/${caseData.CalibrationSessionID}`)}>
            {caseData.CalibrationSessionTitle ?? `Session #${caseData.CalibrationSessionID}`}
          </Typography.Link>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            (ID: {caseData.CalibrationSessionID})
          </Typography.Text>
        </div>
      )}

      {/* Call metadata */}
      <Descriptions
        size="small" bordered column={4} style={{ marginBottom: 16 }}
        labelStyle={{ width: 80, whiteSpace: 'nowrap' }}
        contentStyle={{ wordBreak: 'break-word' }}
      >
        <Descriptions.Item label="Agent">
          {caseData?.AgentName
            ? (
              (currentUser?.CanViewAgentSummary ?? 0) < 1
                ? caseData.AgentName
                : <Typography.Link onClick={() => navigate(
                    caseData.AgentUserID
                      ? `/agents?user_id=${caseData.AgentUserID}`
                      : `/agents?agent_no=${caseData.AgentNo}`
                  )}>
                    {caseData.AgentName}
                  </Typography.Link>
            )
            : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Call Date">{caseData?.CallDate?.slice(0, 10) || '—'}</Descriptions.Item>
        <Descriptions.Item label="Campaign">{caseData?.Campaign || '—'}</Descriptions.Item>
        {/* Call-only fields: an evaluation without a call has none of them. */}
        {hasCall && <Descriptions.Item label="Skill">{caseData?.Skill || '—'}</Descriptions.Item>}
        {hasCall && <Descriptions.Item label="Duration">{formatDuration(caseData?.AgentTime)}</Descriptions.Item>}
        {hasCall && <Descriptions.Item label="Media">{caseData?.MediaName || '—'}</Descriptions.Item>}
        {hasCall && <Descriptions.Item label="Disposition">{caseData?.Disposition || '—'}</Descriptions.Item>}
        {hasCall && <Descriptions.Item label="Direction">{caseData?.Direction || '—'}</Descriptions.Item>}
        {hasCall && <Descriptions.Item label="End Reason">{caseData?.ContactEndReason || '—'}</Descriptions.Item>}
        <Descriptions.Item label="Assigned To">{caseData?.AssignedToName || '—'}</Descriptions.Item>
        {disputeDeadline && (
          <Descriptions.Item label="Ack. Deadline">
            <Typography.Text
              type={dayjs().isAfter(disputeDeadline) ? 'secondary' : undefined}
              style={dayjs().isAfter(disputeDeadline) ? undefined : { color: '#fa8c16' }}>
              {disputeDeadline.format('YYYY-MM-DD')}
              {dayjs().isAfter(disputeDeadline) && ' (passed)'}
            </Typography.Text>
          </Descriptions.Item>
        )}
        {caseData?.AcknowledgedAt && (
          <Descriptions.Item label="Acknowledged On">
            <Typography.Text strong style={{ color: '#389e0d' }}>{formatTS(caseData.AcknowledgedAt)}</Typography.Text>
            {caseData.AcknowledgedByName && (
              <div style={{ fontSize: 12 }}>
                {caseData.AcknowledgedByName === 'Auto' ? 'Auto-acknowledged' : `by ${caseData.AcknowledgedByName}`}
              </div>
            )}
          </Descriptions.Item>
        )}
        <Descriptions.Item label="External Ref" span={2}>
          {canReassign ? (
            <Space.Compact style={{ width: '100%' }}>
              <Input
                value={externalRefDraft}
                onChange={(e) => setExternalRefDraft(e.target.value)}
                placeholder="None"
                style={{ flex: 1 }}
                onPressEnter={handleSaveExternalRef}
                disabled={LOCKED_STATUSES.includes(caseData?.Status)}
              />
              <Button
                onClick={handleSaveExternalRef}
                loading={savingExtRef}
                disabled={LOCKED_STATUSES.includes(caseData?.Status) || externalRefDraft === (caseData?.ExternalRef || '')}
              >
                Save
              </Button>
            </Space.Compact>
          ) : (caseData?.ExternalRef || '—')}
        </Descriptions.Item>
        {caseData?.RescoreNote && (
          <Descriptions.Item label="Dispute Reason" span={2}><strong>{caseData.RescoreNote}</strong></Descriptions.Item>
        )}
      </Descriptions>

      {/* Above the history and notices, so it is read before scrolling on. */}
      {canEdit && LOCKED_STATUSES.includes(caseData?.Status) && (
        <Alert
          type="warning"
          showIcon
          message={`This evaluation is ${caseData.Status === 'canceled' ? 'canceled' : 'completed'} and cannot be edited. Change the status to "Rescore Requested" to reopen it.`}
          style={{ marginBottom: 16 }}
        />
      )}

      {caseData?.AgentName && caseData?.CallDate && (
        <Collapse
          className="no-print"
          defaultActiveKey={[]}
          style={{ marginBottom: 16 }}
          items={[{
            key: 'sec-history',
            label: (
              <span>
                Section History
                <Typography.Text type="secondary" style={{ fontSize: 12, marginLeft: 8, fontWeight: 400 }}>
                  click to expand / collapse
                </Typography.Text>
              </span>
            ),
            children: (
              <>
                <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                  Section occurrences for {caseData.AgentName}
                  {caseData.Campaign && ` · ${caseData.Campaign}`}
                  {secHistoryRange && ` · ${secHistoryRange.start.format('MMM D, YYYY')} — ${secHistoryRange.end.format('MMM D, YYYY')}`}
                  {' '}{caseData.Status === 'completed' ? '(including this evaluation)' : '(excluding this evaluation)'}
                </Typography.Text>
                <Table
                  rowKey="_key"
                  dataSource={secPivotData}
                  columns={secPivotCols}
                  loading={secLoading}
                  size="small"
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                  bordered
                  locale={{ emptyText: 'No completed evaluations in this date range.' }}
                />

                <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', margin: '16px 0 8px' }}>
                  Section History by Evaluation
                </Typography.Text>
                <Table
                  rowKey={(r) => `${r.CaseID}||${r.SectionName}`}
                  dataSource={secCaseRows}
                  loading={secCaseLoading}
                  size="small"
                  pagination={false}
                  scroll={{ x: 'max-content' }}
                  bordered
                  locale={{ emptyText: 'No completed evaluations in this date range.' }}
                  columns={[
                    {
                      title: 'Eval ID', dataIndex: 'CaseID', key: 'CaseID', width: 90, align: 'right',
                      render: (v) => (
                        <Button type="link" size="small" style={{ padding: 0 }}
                          onClick={() => window.open(`/cases/${v}`, '_blank', 'noopener')}>{v}</Button>
                      ),
                    },
                    { title: 'Section', dataIndex: 'SectionName', key: 'SectionName', ellipsis: true, width: 240 },
                    { title: 'Call Date', dataIndex: 'CallDate', key: 'CallDate', width: 110,
                      render: (v) => (v || '').slice(0, 10) || <Typography.Text type="secondary">—</Typography.Text> },
                    { title: 'Created', dataIndex: 'CreatedDate', key: 'CreatedDate', width: 110,
                      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                    {
                      // An evaluation can raise more than one session, so this
                      // is a list rather than a single value.
                      title: 'Coachings', key: 'Coachings', width: 260,
                      render: (_, r) => (r.Coachings || []).length === 0
                        ? <Typography.Text type="secondary">—</Typography.Text>
                        : (r.Coachings || []).map((c) => (
                          <Tag key={c.ID} color={SESSION_STATUS_COLOR[c.Status] ?? 'blue'}
                            style={{ cursor: 'pointer', marginBottom: 2 }}
                            onClick={() => window.open(`/coaching/sessions/${c.ID}`, '_blank', 'noopener')}>
                            #{c.ID}{c.RequestType ? ` · ${c.RequestType}` : ''} · {c.CreatedOn} · {SESSION_STATUS_LABEL[c.Status] ?? c.Status}{c.Linked != null ? (c.Linked ? ' · Linked Sections' : ' · All Sections') : ''}
                          </Tag>
                        )),
                    },
                    { title: 'Score', key: 'ScorePct', width: 90, align: 'right',
                      render: (_, r) => r.ScorePct == null
                        ? <Typography.Text type="secondary">—</Typography.Text>
                        : `${r.ScorePct}%` },
                  ]}
                />
              </>
            ),
          }]}
        />
      )}

      {/* Project tasks about this agent that the viewer is on (hidden when none). */}
      <ProjectFyi page="evaluations" agentUserId={caseData?.AgentUserID} campaignId={caseData?.CampaignID}
        currentUser={currentUser} recordType="evaluation" recordId={caseData?.ID} />

      {caseData?.AgentUserID && !isOwnEvaluation && qaWarnings.length > 0 && (
        <Collapse
          className="no-print attention-collapse"
          defaultActiveKey={[]}
          style={{ marginBottom: 16 }}
          items={[{
            key: 'qa-warnings',
            label: (
              <span>
                Active Compliance QA Related
                <Typography.Text type="secondary" style={{ fontSize: 12, marginLeft: 8, fontWeight: 400 }}>
                  click to expand / collapse
                </Typography.Text>
              </span>
            ),
            children: (
              <Table
                rowKey="ID"
                dataSource={qaWarnings}
                loading={qaWarningsLoading}
                size="small"
                pagination={false}
                scroll={{ x: 'max-content' }}
                bordered
                locale={{ emptyText: 'No active QA related compliance records.' }}
                columns={[
                  {
                    title: 'ID', dataIndex: 'ID', key: 'ID', width: 90, align: 'right',
                    render: (v) => (currentUser?.CanEditIncidents ?? 0) >= 1 ? (
                      <Button type="link" size="small" style={{ padding: 0 }}
                        onClick={() => window.open(`/compliance/incidents/${v}`, '_blank', 'noopener')}>{v}</Button>
                    ) : v,
                  },
                  { title: 'Date of Incident', dataIndex: 'DateOfIncident', key: 'DateOfIncident', width: 130,
                    render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                  { title: 'Warning', dataIndex: 'WarningNumber', key: 'WarningNumber', width: 90,
                    render: (v) => v
                      ? <Tag color={{ '1st': 'blue', '2nd': 'orange', '3rd': 'volcano', 'Final': 'red' }[v]}>{v}</Tag>
                      : <Typography.Text type="secondary">—</Typography.Text> },
                  { title: 'QA Related Expires On', dataIndex: 'QARelatedExpiresOn', key: 'QARelatedExpiresOn', width: 150 },
                  { title: 'Description of Incident', dataIndex: 'Description', key: 'Description', width: 380,
                    onCell: () => ({ style: { whiteSpace: 'pre-wrap' } }),
                    render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                  { title: 'Issues', key: 'Issues', width: 320,
                    render: (_, r) => (r.Issues || []).length
                      ? (r.Issues || []).map((iss) => <Tag key={iss} style={{ marginBottom: 2 }}>{iss}</Tag>)
                      : <Typography.Text type="secondary">—</Typography.Text> },
                  { title: 'Assigned To', dataIndex: 'AssignedToName', key: 'AssignedToName', width: 180,
                    render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                ]}
              />
            ),
          }]}
        />
      )}


      {formData ? (
        <>
          {/* Score summary */}
          <Card style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <div>
                <Typography.Text strong style={{ fontSize: 15 }}>{formData.Name}</Typography.Text>
                {formData.Version && (
                  <Tag style={{ marginLeft: 8 }}>v{formData.Version}</Tag>
                )}
              </div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <Typography.Text type="secondary">Score</Typography.Text>
                  <Typography.Text strong style={{ color: scoreColor }}>
                    {fmtPts(totalEarned)} / {fmtPts(totalMax)} pts ({pct}%)
                  </Typography.Text>
                </div>
                <Progress
                  percent={pct}
                  showInfo={false}
                  strokeColor={scoreColor}
                  size="small"
                />
              </div>
            </div>
          </Card>

          {/* Its own line above the sections it filters, so it is not lost
              at the end of the score card. */}
          {caseData?.Status === 'completed' && (
            <div className="no-print" style={{ marginBottom: 12 }}>
              <Checkbox checked={missedOnly} onChange={(e) => setMissedOnly(e.target.checked)}>
                <Typography.Text strong style={{ fontSize: 14 }}>Show non-full points and occurrences only</Typography.Text>
              </Checkbox>
            </div>
          )}

          {/* Sections */}
          {(formData.Sections || []).map((sec) => {
            const secMax    = (sec.Questions || []).reduce((s, q) => s + questionMaxPts(q), 0);
            const secEarned = (sec.Questions || []).reduce((s, q) => s + (responses[q.ID]?.points_earned ?? 0), 0);
            const isEval    = !caseData?.IsFeedbackOnly && !caseData?.IsCalibration;
            const filtering = missedOnly && caseData?.Status === 'completed';
            const occurrence = (occurrences[sec.ID] ?? 0) === 1;
            const shownQuestions = filtering
              ? (sec.Questions || []).filter((q) => questionMaxPts(q) > 0
                  && (occurrence || (responses[q.ID]?.points_earned ?? 0) < questionMaxPts(q)))
              : (sec.Questions || []);
            if (filtering && shownQuestions.length === 0) return null;
            return (
              <Card
                key={sec.ID}
                title={
                  <Space>
                    <span>{sec.Name}</span>
                    <Tag color="blue">{fmtPts(secEarned)} / {fmtPts(secMax)} pts</Tag>
                  </Space>
                }
                size="small"
                style={{ marginBottom: 12 }}
                styles={{ body: { background: '#f0f7ff' } }}
              >
                {isEval && secMax > 0 && (
                  <div style={{ marginBottom: 14 }}>
                    {isLocked ? (
                      <Typography.Text style={{ fontSize: 13 }}>
                        Occurrence: <strong>{(occurrences[sec.ID] ?? 0) === 1 ? 'Yes' : 'No'}</strong>
                      </Typography.Text>
                    ) : (
                      <>
                        <Checkbox
                          checked={(occurrences[sec.ID] ?? 0) === 1}
                          onChange={(e) =>
                            setOccurrences((prev) => ({ ...prev, [sec.ID]: e.target.checked ? 1 : 0 }))
                          }
                        >
                          <Typography.Text style={{ fontSize: 13 }}>Occurrence</Typography.Text>
                        </Checkbox>
                        <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 2, marginLeft: 24 }}>
                          Check if full credit was given but the agent's performance had an issue
                        </Typography.Text>
                      </>
                    )}
                  </div>
                )}
                {shownQuestions.map((q, qi) => (
                  <div key={q.ID}>
                    {qi > 0 && <Divider style={{ margin: '14px 0', borderColor: '#cce0ff' }} />}

                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: q.Guidance ? 4 : 8 }}>
                      <Space size={4}>
                        <Typography.Text strong>{q.QuestionText}</Typography.Text>
                        {q.IsMandatory ? <Typography.Text type="danger">*</Typography.Text> : null}
                      </Space>
                      <Tag>{responses[q.ID]?.points_earned ?? 0} / {questionMaxPts(q)} pts</Tag>
                    </div>
                    {q.Guidance && (
                      <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                        {q.Guidance}
                      </Typography.Text>
                    )}

                    {q.QuestionType === 'picklist' ? (
                      isLocked ? (
                        <div>
                          <Space direction="vertical" size={2} style={{ marginBottom: 4 }}>
                            {(q.Options || []).map(opt => {
                              const isSelected = opt.ID === responses[q.ID]?.option_id;
                              return (
                                <div key={opt.ID} style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: isSelected ? 1 : 0.4 }}>
                                  <span style={{
                                    width: 13, height: 13, borderRadius: '50%',
                                    border: `2px solid ${isSelected ? '#1677ff' : '#bfbfbf'}`,
                                    background: isSelected ? '#1677ff' : 'transparent',
                                    display: 'inline-block', flexShrink: 0,
                                  }} />
                                  <Typography.Text style={{ fontWeight: isSelected ? 600 : 400 }}>
                                    {opt.OptionText}
                                  </Typography.Text>
                                  <Tag style={{ marginLeft: 2 }}>{opt.Points} pts</Tag>
                                </div>
                              );
                            })}
                          </Space>
                          {responses[q.ID]?.response_text?.trim() && (
                            <Typography.Text style={{ display: 'block', marginTop: 6, color: '#595959', whiteSpace: 'pre-wrap', fontSize: 13 }}>
                              {responses[q.ID].response_text}
                            </Typography.Text>
                          )}
                        </div>
                      ) : (
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Radio.Group
                            value={responses[q.ID]?.option_id ?? null}
                            onChange={(e) => handleOptionSelect(q, e.target.value)}
                          >
                            <Space direction="vertical">
                              {(q.Options || []).map((opt) => (
                                <Radio key={opt.ID} value={opt.ID}>
                                  {opt.OptionText}
                                  <Tag style={{ marginLeft: 8 }}>{opt.Points} pts</Tag>
                                </Radio>
                              ))}
                            </Space>
                          </Radio.Group>
                          <Input.TextArea
                            placeholder="Notes (optional)…"
                            value={responses[q.ID]?.response_text ?? ''}
                            onChange={(e) => setResponse(q.ID, { response_text: e.target.value })}
                            autoSize={{ minRows: 1, maxRows: 4 }}
                            maxLength={4000}
                            showCount
                            style={{ maxWidth: 500 }}
                          />
                        </Space>
                      )
                    ) : q.QuestionType === 'text' ? (
                      isLocked ? (
                        <Typography.Text style={{ whiteSpace: 'pre-wrap' }}>
                          {responses[q.ID]?.response_text?.trim() || '—'}
                        </Typography.Text>
                      ) : (
                        <Input.TextArea
                          placeholder="Enter response…"
                          value={responses[q.ID]?.response_text ?? ''}
                          onChange={(e) => setResponse(q.ID, { response_text: e.target.value })}
                          autoSize={{ minRows: 1, maxRows: 6 }}
                          maxLength={4000}
                          showCount
                          style={{ maxWidth: 500 }}
                        />
                      )
                    ) : q.QuestionType === 'date' ? (
                      isLocked ? (
                        <Typography.Text>{responses[q.ID]?.response_text || '—'}</Typography.Text>
                      ) : (
                        <DatePicker
                          value={responses[q.ID]?.response_text ? dayjs(responses[q.ID].response_text) : null}
                          onChange={(_, dateStr) => setResponse(q.ID, { response_text: dateStr || '' })}
                          style={{ width: 200 }}
                        />
                      )
                    ) : (
                      isLocked ? (
                        <div>
                          <Typography.Text strong>{responses[q.ID]?.points_earned ?? 0} / {q.MaxPoints} pts</Typography.Text>
                          {responses[q.ID]?.response_text?.trim() && (
                            <Typography.Text style={{ display: 'block', marginTop: 6, color: '#595959', whiteSpace: 'pre-wrap', fontSize: 13 }}>
                              {responses[q.ID].response_text}
                            </Typography.Text>
                          )}
                        </div>
                      ) : (
                        <Space wrap>
                          <InputNumber
                            min={0}
                            max={q.MaxPoints}
                            value={responses[q.ID]?.points_earned ?? null}
                            onChange={(v) => setResponse(q.ID, { points_earned: v ?? 0 })}
                            addonAfter={`/ ${q.MaxPoints} pts`}
                            placeholder="0"
                            style={{ width: 160 }}
                          />
                          <Input.TextArea
                            placeholder="Notes (optional)…"
                            value={responses[q.ID]?.response_text ?? ''}
                            onChange={(e) => setResponse(q.ID, { response_text: e.target.value })}
                            autoSize={{ minRows: 1, maxRows: 8 }}
                            maxLength={4000}
                            showCount
                            style={{ width: 500 }}
                          />
                        </Space>
                      )
                    )}
                  </div>
                ))}
              </Card>
            );
          })}

        </>
      ) : (
        <Typography.Text type="secondary">No evaluation form is attached to this evaluation.</Typography.Text>
      )}

      <div className={printComments ? '' : 'no-print'}>
      {/* ── Comments ── */}
      <Divider orientation="left">
        <Space size={6}><CommentOutlined /><span>Comments</span></Space>
      </Divider>

      {comments.length > 0 && (
        <List
          dataSource={comments}
          renderItem={(c) => {
            const canEdit = !c.IsSystem && c.UserID === currentUser?.ID && dayjs().diff(dayjs(c.CreatedAt), 'minute') < 60;
            const canDelete = !c.IsSystem && currentUser?.Role === 'admin';
            const isEditing = editComment.id === c.ID;
            const hideInPrint = printHideAgent && c.UserID === caseData?.AgentUserID;
            return (
              <List.Item className={hideInPrint ? 'no-print' : undefined} style={{ alignItems: 'flex-start', padding: '8px 0' }}>
                <List.Item.Meta
                  avatar={<Avatar icon={<UserOutlined />} size="small" style={{ backgroundColor: '#1677ff', marginTop: 2 }} />}
                  title={
                    <Space size={8}>
                      <Typography.Text strong style={{ fontSize: 13 }}>{c.UserName}</Typography.Text>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>{formatTS(c.CreatedAt)}</Typography.Text>
                      {canEdit && !isEditing && (
                        <Button type="text" size="small" icon={<EditOutlined />}
                          onClick={() => setEditComment({ id: c.ID, text: c.Comment })} />
                      )}
                      {canDelete && !isEditing && (
                        <Popconfirm title="Delete this comment?" onConfirm={() => handleDeleteComment(c.ID)} okText="Delete" okButtonProps={{ danger: true }}>
                          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      )}
                    </Space>
                  }
                  description={isEditing ? (
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Input.TextArea rows={3} value={editComment.text}
                        onChange={(e) => setEditComment(s => ({ ...s, text: e.target.value }))} autoFocus />
                      <Space>
                        <Button type="primary" size="small" onClick={handleSaveComment}>Save</Button>
                        <Button size="small" onClick={() => setEditComment({ id: null, text: '' })}>Cancel</Button>
                      </Space>
                    </Space>
                  ) : (
                    <>
                      <Typography.Text strong={!!c.IsSystem} style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}><LinkifiedText text={c.Comment} /></Typography.Text>
                      <CommentAttachments attachments={c.Attachments} />
                    </>
                  )}
                />
              </List.Item>
            );
          }}
          style={{ marginBottom: 12 }}
        />
      )}

      <div className="no-print">
      {agentCanComment ? (
        <CommentBox
          onSubmit={handleAddComment}
          loading={addingComment}
          value={newComment}
          onChange={setNewComment}
        />
      ) : isAgent && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Comments are only available while the evaluation is completed and within the dispute window, or while a rescore is requested.
        </Typography.Text>
      )}
      </div>{/* end comment input no-print */}
      </div>{/* end printComments wrapper */}

      <div className={printCoaching ? '' : 'no-print'}>
      {/* ── Coaching Sessions ── */}
      <Divider orientation="left">
        <Space size={6}>
          <LinkOutlined />
          <span>Coaching Sessions</span>
          {canManageCoaching && (
            <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setCreateCoachOpen(true)}>
              New Coaching Session
            </Button>
          )}
        </Space>
      </Divider>

      <Table
        rowKey="ID"
        dataSource={coachSessions}
        loading={coachLoading}
        size="small"
        pagination={false}
        locale={{ emptyText: 'No coaching sessions linked to this evaluation.' }}
        style={{ marginBottom: 12 }}
        columns={[
          {
            title: 'Title', dataIndex: 'Title', key: 'Title',
            render: (v, r) => (
              <Typography.Link onClick={() => navigate(`/coaching/sessions/${r.ID}`)}>{v}</Typography.Link>
            ),
          },
          {
            title: 'Status', dataIndex: 'Status', key: 'Status', width: 100,
            render: (v) => {
              const colors = { draft: 'default', active: 'blue', completed: 'green', canceled: 'red' };
              const labels = { draft: 'Draft', active: 'Active', completed: 'Completed', canceled: 'Canceled' };
              return <Tag color={colors[v] ?? 'default'}>{labels[v] ?? v}</Tag>;
            },
          },
          { title: 'Request Type', dataIndex: 'RequestTypeName', key: 'RequestTypeName', width: 160 },
          { title: 'Coach', dataIndex: 'CoachName', key: 'CoachName', width: 160 },
          { title: 'Due Date', dataIndex: 'DueDate', key: 'DueDate', width: 110 },
        ]}
      />
      </div>{/* end printCoaching wrapper */}

      {/* Completion modal - mirrors the error submission flow: the updated
          history including this evaluation, then the coaching decision. Not
          closable, so the choice is made rather than dismissed by accident. */}
      <Modal
        title="Evaluation Completed"
        open={completedModalOpen}
        closable={false}
        maskClosable={false}
        width={780}
        destroyOnClose
        footer={[
          <Button key="not-required" onClick={() => setCompletedModalOpen(false)}>
            Not Required
          </Button>,
          <Button key="coaching" type="primary"
            onClick={() => { setCompletedModalOpen(false); setCreateCoachOpen(true); }}>
            Create Coaching Session
          </Button>,
        ]}
      >
        <Space direction="vertical" style={{ width: '100%', padding: '8px 0' }} size={16}>
          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label="Agent">{caseData?.AgentName || '—'}</Descriptions.Item>
            <Descriptions.Item label="Campaign">{caseData?.Campaign || '—'}</Descriptions.Item>
            <Descriptions.Item label="Call Date">{caseData?.CallDate || '—'}</Descriptions.Item>
            <Descriptions.Item label="Score">
              <Typography.Text strong style={{ color: scoreColor }}>
                {fmtPts(totalEarned)} / {fmtPts(totalMax)} pts ({pct}%)
              </Typography.Text>
              {caseData?.GoalTargetPct != null && (
                <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                  target {caseData.GoalTargetPct}%
                </Typography.Text>
              )}
            </Descriptions.Item>
          </Descriptions>

          {secLoading ? <Spin /> : secPivotData.length > 0 ? (
            <div>
              <Typography.Text strong style={{ display: 'block', marginBottom: 6 }}>
                Section occurrences for {caseData?.AgentName} (including this evaluation)
              </Typography.Text>
              <Table
                rowKey="_key"
                dataSource={secPivotData}
                columns={secPivotCols}
                size="small"
                bordered
                pagination={false}
                scroll={{ x: 'max-content' }}
              />
            </div>
          ) : (
            <Typography.Text type="secondary">
              No section history for this agent in the preceding months.
            </Typography.Text>
          )}
        </Space>
      </Modal>

      <CoachingCreateModal
        open={createCoachOpen}
        agentUserId={caseData?.AgentUserID ?? null}
        agentName={caseData?.AgentName ?? null}
        // Sessions raised from an eval carry its number in the title so they
        // are identifiable from a session list, where the source is not
        // otherwise visible. Matches the same treatment on the error page.
        initialTitle={caseId
          ? `Coaching Session — ${caseData?.AgentName ? `${caseData.AgentName} ` : ''}Eval #${caseId}`
          : undefined}
        initialCampaign={caseData?.Campaign ?? null}
        initialCampaignId={caseData?.CampaignID ?? null}
        campaignLocked={!!caseData?.CampaignID}
        currentUser={currentUser}
        navigate={navigate}
        sourceType="case"
        sourceId={caseId}
        sourceContactId={caseData?.ContactID ?? null}
        onClose={() => setCreateCoachOpen(false)}
      />

      {/* Print options */}
      <Modal maskClosable={false}
        title="Print Options"
        open={printOptOpen}
        onCancel={() => setPrintOptOpen(false)}
        onOk={doPrint}
        okText="Print"
        okButtonProps={{ icon: <PrinterOutlined /> }}
        width={340}
        destroyOnClose
        afterClose={printAfterModalClosed}
      >
        <Space direction="vertical" style={{ padding: '8px 0' }} size={12}>
          <Checkbox checked={printComments} onChange={(e) => setPrintComments(e.target.checked)}>
            Include comments
          </Checkbox>
          <Checkbox style={{ marginLeft: 24 }} disabled={!printComments}
            checked={printHideAgent} onChange={(e) => setPrintHideAgent(e.target.checked)}>
            Hide agent comments
          </Checkbox>
          <Checkbox checked={printCoaching} onChange={(e) => setPrintCoaching(e.target.checked)}>
            Include coaching sessions list
          </Checkbox>
        </Space>
      </Modal>

      <Modal maskClosable={false}
        title="Request Rescore"
        open={rescoreModalOpen}
        onCancel={() => { setRescoreModalOpen(false); setRescoreComment(''); }}
        onOk={handleRequestRescore}
        confirmLoading={requestingRescore}
        okText="Submit Dispute"
        okButtonProps={{ disabled: !rescoreComment.trim() }}
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Please explain why you are requesting a rescore. This comment will be visible to the QA team.
        </Typography.Paragraph>
        <Input.TextArea
          rows={4}
          placeholder="Reason for rescore request…"
          value={rescoreComment}
          onChange={(e) => setRescoreComment(e.target.value)}
          maxLength={4000}
          showCount
          autoFocus
        />
      </Modal>

      {/* Call context, beside the form rather than over it.
          Fixed rather than a Drawer: a Drawer masks the page and traps focus,
          so the evaluation underneath could not be typed into -- which is the
          whole point of having it open. The page pads itself out of the way
          above, so nothing is covered and both halves stay live. */}
      {caseData?.ContactID && (
        <div className="no-print" style={{
          position: 'fixed', top: 64, right: 0, bottom: 0,
          width: callPaneOpen ? '25vw' : 0,
          background: '#fff', borderLeft: callPaneOpen ? '1px solid #f0f0f0' : 'none',
          boxShadow: callPaneOpen ? '-2px 0 8px rgba(0,0,0,0.06)' : 'none',
          overflowY: 'auto', overflowX: 'hidden',
          transition: 'width .15s', zIndex: 5,
        }}>
          {callPaneOpen && (
            <div style={{ padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <Typography.Text strong>Contact {caseData.ContactID}</Typography.Text>
                <Button size="small" type="text" onClick={() => togglePane(false)}>Hide</Button>
              </div>
              <CaseCallPane contactId={caseData.ContactID} isAgent={isAgent} />
            </div>
          )}
        </div>
      )}

      {/* The handle. Always reachable, and the only thing on screen when the
          pane is shut. */}
      {caseData?.ContactID && !callPaneOpen && (
        <Button
          className="no-print"
          size="small"
          icon={<InfoCircleOutlined />}
          onClick={() => togglePane(true)}
          style={{
            position: 'fixed', top: 120, right: 0, zIndex: 5,
            borderTopRightRadius: 0, borderBottomRightRadius: 0,
          }}
        >
          Call Info
        </Button>
      )}
    </div>
  );
}
